import { 
  collection, 
  doc, 
  setDoc, 
  updateDoc, 
  getDoc, 
  getDocs, 
  query, 
  where,
  serverTimestamp,
  addDoc,
  increment,
  writeBatch,
  orderBy
} from 'firebase/firestore';
import { db } from '../firebase';
import { auth } from '../firebase';

// Collection References
const getUserDoc = (userId) => doc(db, 'users', userId);
const getMissedFastsCollection = () => collection(db, 'missedFasts');
const getMissedFastDoc = (fastId) => doc(db, 'missedFasts', fastId);
const getMessagesCollection = () => collection(db, 'messages');

// Message Operations
export const sendMessage = async (userId, messageData) => {
  try {
    const messagesRef = getMessagesCollection();
    await addDoc(messagesRef, {
      userId,
      ...messageData,
      status: 'PENDING', // PENDING, SENT, FAILED
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error sending message:', error);
    throw error;
  }
};

// User Profile Operations
export const createUserProfile = async (userId, phoneNumber) => {
  // First check if user exists
  const userRef = getUserDoc(userId);
  const userDoc = await getDoc(userRef);
  const isNewUser = !userDoc.exists();

  // Create or update the user profile
  await setDoc(userRef, {
    phoneNumber,
    totalMissedFasts: isNewUser ? 0 : userDoc.data().totalMissedFasts,
    totalMakeupNeeded: isNewUser ? 0 : userDoc.data().totalMakeupNeeded,
    createdAt: isNewUser ? serverTimestamp() : userDoc.data().createdAt,
    updatedAt: serverTimestamp()
  }, { merge: true });

  // Only send welcome message for new users
  if (isNewUser) {
    // Try to send welcome message to internal message collection
    try {
      await sendMessage(userId, {
        type: 'WELCOME',
        content: 'Assalamu alaikum! Welcome to the Ramadan Makeup Fast Tracker. We\'ll help you keep track of your makeup fasts and send you friendly reminders. May Allah make it easy for you to complete your fasts.',
        scheduledFor: serverTimestamp()
      });
    } catch (error) {
      console.error('Error sending welcome message to messages collection:', error);
      // Don't throw the error since messages are non-critical
    }

    // Try to send welcome SMS
    try {
      const functionUrl = 'https://send-ramadan-sms-bgpyuerq3q-uc.a.run.app';
      console.log('Attempting to send SMS to:', phoneNumber);
      
      // Get the current user's ID token
      const idToken = await auth.currentUser.getIdToken();
      
      const requestBody = {
        to: phoneNumber,
        message: 'Assalamu alaikum! Welcome to the Ramadan Makeup Fast Tracker. We\'ll help you keep track of your makeup fasts and send you friendly reminders. May Allah make it easy for you to complete your fasts.'
      };
      console.log('Request body:', requestBody);
      
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        mode: 'cors',
        body: JSON.stringify(requestBody)
      });
      
      console.log('Response status:', response.status);
      console.log('Response headers:', Object.fromEntries(response.headers.entries()));
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        console.error('Error response data:', errorData);
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Welcome SMS sent successfully:', data);
    } catch (error) {
      console.error('Error sending welcome SMS:', error);
      console.error('Error details:', {
        name: error.name,
        message: error.message,
        stack: error.stack
      });
      // Don't throw the error since this is a non-critical feature
    }
  }
};

// Missed Fasts Operations
export const addMissedFast = async (userId, fastData) => {
  try {
    // First create the fast document
    const fastsRef = getMissedFastsCollection();
    const docRef = await addDoc(fastsRef, {
      ...fastData,
      userId,
      status: 'MISSED',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    
    // Then update the user's totals
    const userRef = getUserDoc(userId);
    await updateDoc(userRef, {
      totalMissedFasts: increment(1),
      totalMakeupNeeded: increment(1),
      updatedAt: serverTimestamp()
    });
    
    return docRef.id;
  } catch (error) {
    console.error('Error in addMissedFast:', error);
    throw error;
  }
};

export const getMissedFasts = async (userId) => {
  try {
    const fastsRef = getMissedFastsCollection();
    const q = query(
      fastsRef,
      where('userId', '==', userId),
      orderBy('date', 'desc')
    );
    
    try {
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      // Check if the error is due to missing index
      if (error.code === 'failed-precondition' && error.message.includes('index')) {
        console.error('Missing index error:', error);
        throw new Error(
          'Database setup required. Please create the following index in Firebase Console:\n' +
          'Collection: missedFasts\n' +
          'Fields to index: userId (Ascending) and date (Descending)'
        );
      }
      throw error;
    }
  } catch (error) {
    console.error('Error in getMissedFasts:', error);
    throw error;
  }
};

export const toggleFastStatus = async (userId, fastId) => {
  try {
    const fastRef = getMissedFastDoc(fastId);
    const fastDoc = await getDoc(fastRef);

    if (!fastDoc.exists()) {
      throw new Error('Fast not found');
    }

    // Check if the user owns this fast
    if (fastDoc.data().userId !== userId) {
      throw new Error('Unauthorized: You can only update your own fasts');
    }

    const currentStatus = fastDoc.data().status;
    const userRef = getUserDoc(userId);

    if (currentStatus === 'MISSED') {
      // Mark as made up
      await updateDoc(fastRef, {
        status: 'MADE_UP',
        completedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      // Decrement makeup needed count
      await updateDoc(userRef, {
        totalMakeupNeeded: increment(-1),
        updatedAt: serverTimestamp()
      });
    } else {
      // Mark back as missed
      await updateDoc(fastRef, {
        status: 'MISSED',
        completedAt: null,
        updatedAt: serverTimestamp()
      });

      // Increment makeup needed count
      await updateDoc(userRef, {
        totalMakeupNeeded: increment(1),
        updatedAt: serverTimestamp()
      });
    }
  } catch (error) {
    console.error('Error toggling fast status:', error);
    throw error;
  }
};

// Mark the old function as deprecated but keep it for backward compatibility
export const markFastAsMadeUp = async (userId, fastId) => {
  console.warn('markFastAsMadeUp is deprecated. Please use toggleFastStatus instead.');
  return toggleFastStatus(userId, fastId);
};

// Constants for predefined reasons
export const FAST_REASONS = {
  HEALTH: {
    SICK: 'Sick/Illness',
    MEDICATION: 'Medication Requirements',
    PREGNANCY: 'Pregnancy',
    NURSING: 'Nursing/Breastfeeding',
    MEDICAL_PROCEDURE: 'Medical Procedure/Surgery'
  },
  TRAVEL: {
    TRAVELING: 'Traveling',
    LONG_JOURNEY: 'Long Journey',
    WORK_TRAVEL: 'Work Travel'
  },
  PHYSICAL: {
    MENSTRUATION: 'Menstruation',
    DEHYDRATION: 'Extreme Thirst/Dehydration',
    HARD_WORK: 'Physical Labor/Hard Work Conditions',
    HEAT: 'Heat Exhaustion'
  },
  OTHER: {
    ELDERLY: 'Elderly/Chronic Condition',
    MISSED_SUHOOR: 'Forgot to Wake Up for Suhoor',
    ACCIDENTAL: 'Accidentally Broke Fast',
    EMERGENCY: 'Emergency Situation'
  }
};

export const FAST_STATUS = {
  MISSED: 'MISSED',
  MADE_UP: 'MADE_UP'
};

export const addBulkMissedFasts = async (userId, startDate, endDate, fastData) => {
  try {
    const batch = writeBatch(db);
    const userRef = getUserDoc(userId);
    const dates = [];
    
    // Generate array of dates between start and end date
    let currentDate = new Date(startDate);
    const lastDate = new Date(endDate);
    
    while (currentDate <= lastDate) {
      dates.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    // Create a document for each date
    const fastsRef = getMissedFastsCollection();
    const numberOfFasts = dates.length;
    
    dates.forEach(date => {
      const newFastRef = doc(fastsRef);
      batch.set(newFastRef, {
        ...fastData,
        date: date.toISOString(),
        userId,
        status: 'MISSED',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    });
    
    // Update user's totals
    batch.update(userRef, {
      totalMissedFasts: increment(numberOfFasts),
      totalMakeupNeeded: increment(numberOfFasts),
      updatedAt: serverTimestamp()
    });
    
    await batch.commit();
    return numberOfFasts;
  } catch (error) {
    console.error('Error in addBulkMissedFasts:', error);
    throw error;
  }
};

export const deleteMissedFast = async (userId, fastId) => {
  try {
    const fastRef = getMissedFastDoc(fastId);
    const fastDoc = await getDoc(fastRef);

    if (!fastDoc.exists()) {
      throw new Error('Fast not found');
    }

    // Check if the user owns this fast
    if (fastDoc.data().userId !== userId) {
      throw new Error('Unauthorized: You can only delete your own fasts');
    }

    const batch = writeBatch(db);
    const userRef = getUserDoc(userId);

    // Delete the fast document
    batch.delete(fastRef);

    // Update user's totals
    // If the fast was still marked as missed, decrement the makeup needed count
    if (fastDoc.data().status === 'MISSED') {
      batch.update(userRef, {
        totalMissedFasts: increment(-1),
        totalMakeupNeeded: increment(-1),
        updatedAt: serverTimestamp()
      });
    } else {
      // If the fast was already made up, only decrement total missed fasts
      batch.update(userRef, {
        totalMissedFasts: increment(-1),
        updatedAt: serverTimestamp()
      });
    }

    await batch.commit();
  } catch (error) {
    console.error('Error deleting fast:', error);
    throw error;
  }
}; 